<script setup lang="ts">
import { computed, watch, ref } from "vue"
import { toolkitUrls } from '@/util/links.js'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import Tooltip from '@/components/tooltip/Tooltip.vue'
import TooltipProvider from '@/components/tooltip/TooltipProvider.vue'
import TooltipTrigger from '@/components/tooltip/TooltipTrigger.vue'
import TooltipContent from '@/components/tooltip/TooltipContent.vue'
import { usePresentLevelsStore } from './presentlevels.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow.vue'
import { Goal } from '../goals/goal_types'
import GoalCardLevelIndicators from '@/goals/GoalCardLevelIndicators.vue'
import { AccessPointsUtil } from '@/util/access_points'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { PRESENTLEVELS_EVENT_NAMES as EVENT_NAMES } from './event_names'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable.js'
import { PLWGradeLabelToBrowseGoalsValue } from '@/util/gradesort.js'

const props = defineProps({
  summaryUUID: { type: String, default: '' },
  parentCollapsed: { type: Boolean, default: false },
})

const presentLevelsStore = usePresentLevelsStore()
const visibilityStore = useVisibilityStore()
const summary = computed(() => presentLevelsStore.subjectSummaries.find(s => s.uuid === props.summaryUUID))
const statementLoading = computed(() => presentLevelsStore.isStatementGeneratingForSummaryUUID(props.summaryUUID))
const alignedGoalsLoading = computed(() => presentLevelsStore.isLoadingAlignedGoalsForSummaryUUID(props.summaryUUID))
const loading = computed(() => statementLoading.value || alignedGoalsLoading.value)
const noNewAlignedGoals = computed(() => presentLevelsStore.noNewAlignedGoalsForSummaryUUID(props.summaryUUID))

const alignedGoalsData = computed(() => {
  const summaryAlignedGoals = presentLevelsStore.getAlignedGoalsForSummary(props.summaryUUID) as Array<Record<string, Goal[]>> || []
  

  let total = 0
  for (const domainGoals of summaryAlignedGoals) {
    if (!domainGoals) continue
    for (const goals of Object.values(domainGoals)) {
      if (Array.isArray(goals)) {
        total += goals.length
      }
    }
  }
  
  return { alignedGoals: summaryAlignedGoals, total }
})

const totalGoalsCount = computed(() => alignedGoalsData.value.total)
const hasAlignedGoals = computed(() => totalGoalsCount.value > 0)

const emptyStateTooltipContent = 'Develop a present levels statement to the left in order to view aligned goals.'
const errorStateTooltipContent = computed(() => 
  hasAlignedGoals.value ? 'No additional aligned goals found.' : 'No aligned goals found.'
)
const buildBrowseUrl = (domain: string, goals: Goal[]) => {
  const subjectValue = goals.length > 0 && goals[0].subject ? goals[0].subject : (summary.value?.subject_label || '')
  const subject = getGoalSubjectLabelFromValue(subjectValue)
  const gradeLabel = summary.value?.grade_label || ''
  const browseGoalsGrade = PLWGradeLabelToBrowseGoalsValue(gradeLabel)
  
  return `${toolkitUrls.browse}?subject=${encodeURIComponent(subject)}&subjectDomain=${encodeURIComponent(domain)}&grades=${encodeURIComponent(browseGoalsGrade)}`
}

const getLevelLabel = (accessPointLevel: string | undefined): string => {
  if (!accessPointLevel) return ''
  return AccessPointsUtil.labelsMap[accessPointLevel] || ''
}

const analytics = getAnalyticsTracker()

const handleGoalClick = (goal: Goal) => {
  analytics.sendEvent(EVENT_NAMES.ALIGNED_GOALS.CLICKED, {
    goal_title: goal.title,
    goal_uuid: goal.uuid,
    goal_subject: summary.value?.subject_label || '',
    goal_subject_domains: goal.subject_domains?.join(', ') || '',
    goal_access_point_level: goal.access_point_level || '',
  })
}

const handleBrowseMoreClick = (domain: string) => {
  analytics.sendEvent(EVENT_NAMES.ALIGNED_GOALS.BROWSE_MORE, {
    browse_goals_filter_subject: summary.value?.subject_label || '',
    browse_goals_filter_category: domain,
    browse_goals_filter_grades: summary.value?.grade_label || '',
  })
}

const isCollapsed = ref(false)
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
}

// Watch parent collapse state and sync when parent collapses/expands
watch(() => props.parentCollapsed, (newValue) => {
  isCollapsed.value = newValue
})
</script>

<template>
  <div class="w-full md:w-plw-subcontent-panel ml-0 md:ml-4">
    <TooltipProvider v-if="!hasAlignedGoals && !statementLoading" :delayDuration="0">
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            tabindex="0"
            :aria-label="emptyStateTooltipContent"
            class="flex items-center rounded-lg border bg-gray-50 hover:bg-gray-300 border-gray-400 py-3 px-4 text-md cursor-pointer transition-colors"
            :class="{ 'rounded-b-none': noNewAlignedGoals }"
            @click="toggleCollapse"
          >
            <span 
              class="material-icons-round text-2xl transition-transform duration-200 mr-2"
              :class="{ '-rotate-90': isCollapsed }"
            >
              expand_more
            </span>
            <h2 class="font-medium mr-2">ALIGNED GOALS</h2>
            <div v-if="!statementLoading" class="font-bold bg-blue-200  text-blue-400 rounded-sm px-2">0</div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="left" :sideOffset="5" class="max-w-[300px] text-md p-3 rounded-sm">
          <p>{{ emptyStateTooltipContent }}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>

    <div 
      v-else 
      tabindex="0"
      role="button"
      aria-label="Toggle aligned goals panel"
      :aria-expanded="!isCollapsed"
      class="flex items-center rounded-t-lg border bg-gray-50 hover:bg-gray-300 border-gray-400 py-3 px-4 text-md cursor-pointer transition-colors" 
      @click="toggleCollapse"
      @keydown.enter="toggleCollapse"
      @keydown.space.prevent="toggleCollapse"
    >
      <span 
        class="material-icons-round text-2xl transition-transform duration-200 mr-2"
        :class="{ '-rotate-90': isCollapsed }"
      >
        expand_more
      </span>
      <h2 class="font-medium mr-2">ALIGNED GOALS</h2>
      <div v-if="!statementLoading" class="font-bold bg-blue-200  text-blue-400 rounded-sm px-2">{{ totalGoalsCount }}</div>
    </div>

    <div id="aligned-goals-loading-initial" v-if="statementLoading || (alignedGoalsLoading && !hasAlignedGoals)" class="flex flex-col gap-3 rounded-b-lg border-b border-x border-gray-400 p-4 text-md">
      <LoadingSkeletonRow 
        v-for="(_, index) in Array(4)"
        :key="`skeleton-${index}`"
        :possible-widths="['w-1/2', 'w-3/5', 'w-2/3', 'w-3/4', '4/5']"
        height="h-6"
        color="bg-gray-300" 
        border-radius="rounded-xs" 
      />
    </div>

    <div
      v-if="noNewAlignedGoals && !statementLoading"
      tabindex="0"
      :aria-label="errorStateTooltipContent"
      class="flex items-center border-x border-gray-400 px-4 pt-4 text-md"
      :class="{ 'border-b border-gray-400 pb-4 rounded-b-lg': noNewAlignedGoals && !hasAlignedGoals }"
    >
      <span class="bg-blue-200 w-full p-3 rounded-sm">{{ errorStateTooltipContent }}</span>
    </div>

    <div
      v-if="!statementLoading && hasAlignedGoals"
      v-show="!isCollapsed"
      class="border-b border-x border-gray-400 p-4 text-md"
      :class="{ 'rounded-b-lg': !alignedGoalsLoading && !noNewAlignedGoals, 'rounded-b-none': noNewAlignedGoals }"
    >
      <div v-for="domainGoals in alignedGoalsData.alignedGoals" :key="Object.keys(domainGoals)[0]" class="mb-4">
        <h3 class="font-bold text-xs text-gray-700">{{ Object.keys(domainGoals)[0].toUpperCase() }}</h3>
        <ul class="ml-4 text-blue-400 font-medium text-sm">
          <li v-for="goal in Object.values(domainGoals)[0]" :key="goal.uuid" class="list-disc mb-1 ml-1">
            <TooltipProvider :delayDuration="0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <a :id="`goal-link-${goal.uuid}`" :href="toolkitUrls.anchorPage(goal.uuid)" target="_blank" rel="noopener noreferrer" class="cursor-pointer hover:underline text-md" @click="handleGoalClick(goal)">
                    <span>{{ goal.title }}</span>
                    <OpensInNewTabIcon class="ml-0.5 leading-none align-text-bottom" />
                  </a>
                </TooltipTrigger>
                <TooltipContent class="max-w-[300px] text-md p-3 rounded-sm">
                  <p>{{ goal.plain_goal_text }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div v-if="goal.access_point_level && goal.grades && goal.grades.length > 0" class="mb-3">
              <GoalCardLevelIndicators
                :grades="goal.grades"
                :level="goal.access_point_level"
                :level-label="getLevelLabel(goal.access_point_level)"
                :super-page="goal.super_page"
                variant="aligned-goals"
              />
            </div>
          </li>
        </ul>
      </div>

      <div class="mt-4">
        <h3 class="font-bold text-xs text-gray-700">BROWSE MORE GOALS IN:</h3>
        <ul class="ml-4 text-blue-400 text-sm">
          <li
            v-for="(domainGoals, index) in alignedGoalsData.alignedGoals"
            :key="`${Object.keys(domainGoals)[0]}-browse-${index}`"
            class="list-disc mb-1 ml-1 text-md font-medium"
          >
            <a :href="buildBrowseUrl(Object.keys(domainGoals)[0], Object.values(domainGoals)[0])" target="_blank" rel="noopener noreferrer" class="cursor-pointer hover:underline" @click="handleBrowseMoreClick(Object.keys(domainGoals)[0])">
              {{ Object.keys(domainGoals)[0] }}
              <OpensInNewTabIcon class="ml-0.5 leading-none align-text-bottom" />
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Loading indicator for aligned goals while existing goals are visible -->
    <div id="aligned-goals-loading-additional" v-if="alignedGoalsLoading && hasAlignedGoals" v-show="!isCollapsed" class="flex flex-col gap-3 border-b border-x border-gray-400 p-4 text-md rounded-b-lg">
      <LoadingSkeletonRow 
        v-for="(_, index) in Array(2)"
        :key="`aligned-skeleton-${index}`"
        :possible-widths="['w-1/2', 'w-3/5', 'w-2/3', 'w-3/4', '4/5']"
        height="h-6"
        color="bg-gray-300" 
        border-radius="rounded-xs" 
      />
    </div>
  </div>
</template>
